// FILE: src/admin/pages/support/owners/tabs/SupportOwnerVehiclesTab.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { listSupportOwnerVehicles } from "../../../../../api/adminSupportApi";

function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}

function safeStr(x) {
  return x == null ? "" : String(x);
}

function n(x) {
  const v = Number(x);
  return Number.isFinite(v) ? v : 0;
}

function fmtInt(x) {
  return n(x).toLocaleString("en-IN", { maximumFractionDigits: 0 });
}

function Chip({ children, tone = "neutral" }) {
  const cls =
    tone === "good"
      ? "border-emerald-200 bg-emerald-50 text-emerald-800"
      : tone === "bad"
      ? "border-rose-200 bg-rose-50 text-rose-800"
      : tone === "warn"
      ? "border-amber-200 bg-amber-50 text-amber-800"
      : "border-slate-200 bg-slate-50 text-slate-700";
  return (
    <span
      className={classNames(
        "inline-flex items-center rounded-md border px-3 py-1.5 text-[11px] font-semibold",
        cls
      )}
    >
      {children}
    </span>
  );
}

function Footer({ page, size, total, totalPages, canPrev, canNext, setPage, setSize }) {
  const from = total ? page * size + 1 : 0;
  const to = total ? Math.min(total, (page + 1) * size) : 0;

  return (
    <div className="px-5 py-3 border-t border-slate-200 bg-white">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="text-xs text-slate-500">
          {total ? (
            <>
              Showing{" "}
              <span className="font-semibold text-slate-700">{from}</span>–{" "}
              <span className="font-semibold text-slate-700">{to}</span> of{" "}
              <span className="font-semibold text-slate-700">{fmtInt(total)}</span>
            </>
          ) : (
            "No records"
          )}
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="text-xs text-slate-500">View</div>
            <div className="relative">
              <select
                value={size}
                onChange={(e) => setSize(Number(e.target.value))}
                className="appearance-none h-10 rounded-md border border-slate-200 bg-white pl-3 pr-9 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                {[10, 20, 30, 50, 100].map((nn) => (
                  <option key={nn} value={nn}>
                    {nn} / page
                  </option>
                ))}
              </select>
              <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500">
                ▼
              </span>
            </div>
          </div>

          <div className="text-sm text-slate-700">
            Page <span className="font-semibold">{page + 1}</span> of{" "}
            <span className="font-semibold">{Math.max(1, totalPages || 1)}</span>
          </div>

          <div className="flex items-center gap-2">
            <button
              className="h-10 px-4 rounded-md border border-slate-200 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
              disabled={!canPrev}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              Prev
            </button>
            <button
              className="h-10 px-4 rounded-md border border-slate-200 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
              disabled={!canNext}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------- export helpers -------------------- */

function csvEscape(v) {
  const s = safeStr(v);
  if (s.includes('"') || s.includes(",") || s.includes("\n") || s.includes("\r")) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function downloadCsv(filename, rows, columns) {
  const header = columns.map((c) => csvEscape(c.label)).join(",");
  const body = rows.map((r) => columns.map((c) => csvEscape(c.get(r))).join(",")).join("\n");
  const csv = `${header}\n${body}\n`;

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();

  URL.revokeObjectURL(url);
}

function IconSearch({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
      <path d="M10.5 18.5a8 8 0 1 1 0-16 8 8 0 0 1 0 16Z" stroke="currentColor" strokeWidth="2" />
      <path d="M16.5 16.5 21 21" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

function IconDownload({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
      <path d="M12 3v10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M8 11l4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5 21h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

export default function SupportOwnerVehiclesTab() {
  const { ownerId } = useOutletContext();

  const [page, setPage] = useState(0);
  const [size, setSize] = useState(30);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // ✅ search
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState(""); // debounced query
  const qTimerRef = useRef(null);

  // ✅ export menu
  const [exportOpen, setExportOpen] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);
  const exportRef = useRef(null);

  const reqRef = useRef(0);

  useEffect(() => {
    if (qTimerRef.current) clearTimeout(qTimerRef.current);
    qTimerRef.current = setTimeout(() => {
      setPage(0);
      setQ(String(qInput || "").trim());
    }, 300);
    return () => {
      if (qTimerRef.current) clearTimeout(qTimerRef.current);
    };
  }, [qInput]);

  useEffect(() => {
    function onDocClick(e) {
      if (!exportRef.current) return;
      if (!exportRef.current.contains(e.target)) setExportOpen(false);
    }
    if (exportOpen) document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [exportOpen]);

  useEffect(() => {
    let alive = true;

    (async () => {
      const rid = ++reqRef.current;
      setBusy(true);
      setErr("");

      try {
        const res = await listSupportOwnerVehicles({ ownerId, q, page, size });
        if (!alive || rid !== reqRef.current) return;

        const content = Array.isArray(res?.content) ? res.content : [];
        setRows(content);
        setTotal(Number(res?.totalElements ?? content.length ?? 0));
        setTotalPages(Number(res?.totalPages ?? 0));
      } catch (e) {
        if (!alive) return;
        setErr(e?.message || "Failed to load vehicles");
        setRows([]);
        setTotal(0);
        setTotalPages(0);
      } finally {
        if (alive) setBusy(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [ownerId, page, size, q]);

  const canPrev = page > 0;
  const canNext = totalPages ? page + 1 < totalPages : rows.length === size;

  const colgroup = useMemo(
    () => (
      <colgroup>
        <col style={{ width: "140px" }} />
        <col style={{ width: "240px" }} />

        {/* ✅ NEW: Year of purchase */}
        <col style={{ width: "130px" }} />

        <col style={{ width: "230px" }} />
        <col style={{ width: "220px" }} />
        <col style={{ width: "180px" }} />

        {/* counters */}
        <col style={{ width: "110px" }} />
        <col style={{ width: "110px" }} />
        <col style={{ width: "120px" }} />
        <col style={{ width: "130px" }} />
        <col style={{ width: "120px" }} />
      </colgroup>
    ),
    []
  );

  const viewRows = useMemo(() => {
    const qq = String(q || "").toLowerCase();
    if (!qq) return rows;

    return rows.filter((r) => {
      const hay = [
        r.regNo,
        r.brand,
        r.model,
        r.color,

        // ✅ NEW searchable field
        r.yearOfPurchase,

        r.vehicleLocation,
        r.openForBookingStates,
        r.latitude,
        r.longitude,
        r.complianceVerified ? "verified yes" : "verified no",
        r.complianceComplete ? "complete yes" : "complete no",
      ]
        .map((x) => safeStr(x).toLowerCase())
        .join(" | ");
      return hay.includes(qq);
    });
  }, [rows, q]);

  const csvColumns = useMemo(
    () => [
      { label: "Reg No", get: (r) => r.regNo ?? "" },
      { label: "Brand", get: (r) => r.brand ?? "" },
      { label: "Model", get: (r) => r.model ?? "" },
      { label: "Color", get: (r) => r.color ?? "" },

      // ✅ NEW
      { label: "Year of Purchase", get: (r) => r.yearOfPurchase ?? "" },

      { label: "Compliance Verified", get: (r) => (r.complianceVerified ? "YES" : "NO") },
      { label: "Compliance Complete", get: (r) => (r.complianceComplete ? "YES" : "NO") },
      { label: "Location", get: (r) => r.vehicleLocation ?? "" },
      { label: "Latitude", get: (r) => (r.latitude == null ? "" : String(r.latitude)) },
      { label: "Longitude", get: (r) => (r.longitude == null ? "" : String(r.longitude)) },
      { label: "Open For Booking", get: (r) => r.openForBookingStates ?? "" },
      { label: "Requests", get: (r) => fmtInt(r.requestCount) },
      { label: "Bookings", get: (r) => fmtInt(r.bookingCount) },
      { label: "Completed", get: (r) => fmtInt(r.completedBookingCount) },
      { label: "Unfulfilled", get: (r) => fmtInt(r.unfulfilledBookingCount) },
      { label: "Upcoming", get: (r) => fmtInt(r.upcomingBookingCount) },
    ],
    []
  );

  function filenameBase() {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const qs = q ? `_q-${q.replace(/\s+/g, "-").slice(0, 24)}` : "";
    return `owner_${ownerId}_vehicles${qs}_${yyyy}-${mm}-${dd}`;
  }

  async function exportCurrentPageCsv() {
    setExportOpen(false);
    const file = `${filenameBase()}_page-${page + 1}.csv`;
    downloadCsv(file, viewRows, csvColumns);
  }

  async function exportAllCsv() {
    setExportOpen(false);
    setExportBusy(true);

    try {
      const MAX_ROWS = 5000;
      const pageSize = 200;

      let p = 0;
      let out = [];
      let tp = null;

      while (true) {
        const res = await listSupportOwnerVehicles({ ownerId, q, page: p, size: pageSize });
        const content = Array.isArray(res?.content) ? res.content : [];
        out = out.concat(content);

        tp = Number(res?.totalPages ?? tp ?? 0);

        if (out.length >= MAX_ROWS) {
          out = out.slice(0, MAX_ROWS);
          break;
        }

        if (tp && p + 1 >= tp) break;
        if (!tp && content.length < pageSize) break;
        p += 1;
      }

      const filtered = (() => {
        const qq = String(q || "").toLowerCase();
        if (!qq) return out;
        return out.filter((r) => {
          const hay = [
            r.regNo,
            r.brand,
            r.model,
            r.color,
            r.yearOfPurchase, // ✅ NEW
            r.vehicleLocation,
            r.openForBookingStates,
            r.latitude,
            r.longitude,
            r.complianceVerified ? "verified yes" : "verified no",
            r.complianceComplete ? "complete yes" : "complete no",
          ]
            .map((x) => safeStr(x).toLowerCase())
            .join(" | ");
          return hay.includes(qq);
        });
      })();

      const file = `${filenameBase()}_ALL.csv`;
      downloadCsv(file, filtered, csvColumns);
    } catch (e) {
      setErr(e?.message || "Export failed");
    } finally {
      setExportBusy(false);
    }
  }

  return (
    <div className="min-w-0">
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        {/* Header */}
        <div className="border-b border-slate-200 px-5 py-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="text-sm font-extrabold text-slate-900">Vehicles</div>
              <div className="mt-1 text-xs text-slate-500">
                Owner’s registered vehicles + quick booking/request counters.
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
              <div className="relative w-full sm:w-[340px]">
                <IconSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  value={qInput}
                  onChange={(e) => setQInput(e.target.value)}
                  placeholder="Search reg, model, year, location…"
                  className="w-full h-10 rounded-md border border-slate-200 bg-white pl-9 pr-10 text-sm font-semibold text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
                />
                {qInput ? (
                  <button
                    onClick={() => setQInput("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-7 px-2 rounded-md text-slate-600 hover:bg-slate-50 text-xs font-bold"
                    title="Clear"
                  >
                    ✕
                  </button>
                ) : null}
              </div>

              <div className="relative" ref={exportRef}>
                <button
                  type="button"
                  disabled={exportBusy || busy || viewRows.length === 0}
                  onClick={() => setExportOpen((v) => !v)}
                  className="h-10 px-4 rounded-md border border-slate-200 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50 inline-flex items-center gap-2"
                >
                  <IconDownload className="h-4 w-4 text-slate-600" />
                  {exportBusy ? "Exporting…" : "Export"}
                  <span className="text-slate-500">▼</span>
                </button>

                {exportOpen ? (
                  <div className="absolute right-0 mt-2 w-[220px] rounded-xl border border-slate-200 bg-white shadow-lg overflow-hidden z-20">
                    <button
                      className="w-full text-left px-4 py-3 text-sm font-semibold text-slate-800 hover:bg-slate-50"
                      onClick={exportCurrentPageCsv}
                    >
                      CSV — Current page
                      <div className="mt-0.5 text-xs text-slate-500">Exports rows currently visible</div>
                    </button>
                    <button
                      className="w-full text-left px-4 py-3 text-sm font-semibold text-slate-800 hover:bg-slate-50"
                      onClick={exportAllCsv}
                    >
                      CSV — All vehicles
                      <div className="mt-0.5 text-xs text-slate-500">Fetches all pages (cap 5,000 rows)</div>
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        {err ? (
          <div className="px-5 py-3 border-b border-slate-200 text-sm font-semibold text-rose-600">{err}</div>
        ) : null}

        {/* Table */}
        <div className="w-full overflow-x-auto">
          <table className="min-w-[1430px] w-full table-fixed">
            {colgroup}

            <thead className="bg-slate-50">
              <tr className="border-b border-slate-200">
                <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">
                  Reg No
                </th>
                <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">
                  Vehicle
                </th>

                {/* ✅ NEW */}
                <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">
                  Year
                </th>

                <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">
                  Compliance
                </th>
                <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">
                  Location
                </th>
                <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">
                  Open for booking
                </th>

                <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wider text-slate-600">
                  Requests
                </th>
                <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wider text-slate-600">
                  Bookings
                </th>
                <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wider text-slate-600">
                  Completed
                </th>
                <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wider text-slate-600">
                  Unfulfilled
                </th>
                <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wider text-slate-600">
                  Upcoming
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-200">
              {busy ? (
                <tr>
                  <td colSpan={11} className="px-5 py-8 text-sm text-slate-600">
                    Loading…
                  </td>
                </tr>
              ) : viewRows.length === 0 ? (
                <tr>
                  <td colSpan={11} className="px-5 py-10 text-center">
                    <div className="text-sm font-semibold text-slate-900">No vehicles</div>
                    <div className="mt-1 text-xs text-slate-500">
                      {q ? "No vehicles matched your search." : "This owner has not added vehicles yet."}
                    </div>
                  </td>
                </tr>
              ) : (
                viewRows.map((r, idx) => {
                  const verified = !!r.complianceVerified;
                  const complete = !!r.complianceComplete;

                  const requestCount = n(r.requestCount);
                  const bookingCount = n(r.bookingCount);
                  const completed = n(r.completedBookingCount);
                  const unfulfilled = n(r.unfulfilledBookingCount);
                  const upcoming = n(r.upcomingBookingCount);

                  const vehicleTitle = `${safeStr(r.brand)} ${safeStr(r.model)}`.trim();

                  return (
                    <tr key={r.regNo ?? idx} className={idx % 2 === 0 ? "bg-white" : "bg-slate-50/30"}>
                      <td className="px-4 py-4">
                        <div className="text-sm font-semibold text-slate-900 tabular-nums">
                          {r.regNo ?? "—"}
                        </div>
                      </td>

                      <td className="px-4 py-4">
                        <div className="text-sm font-semibold text-slate-900 truncate">
                          {vehicleTitle || "—"}
                        </div>
                        <div className="mt-0.5 text-xs text-slate-500 truncate">{r.color || "—"}</div>
                      </td>

                      {/* ✅ NEW */}
                      <td className="px-4 py-4">
                        <div className="text-sm font-semibold text-slate-900 tabular-nums">
                          {r.yearOfPurchase || "—"}
                        </div>
                      </td>

                      <td className="px-4 py-4">
                        <div className="flex flex-wrap gap-2">
                          <Chip tone={verified ? "good" : "neutral"}>Verified: {verified ? "YES" : "NO"}</Chip>
                          <Chip tone={complete ? "good" : "bad"}>Complete: {complete ? "YES" : "NO"}</Chip>
                        </div>
                      </td>

                      <td className="px-4 py-4">
                        <div className="text-xs font-semibold text-slate-700 truncate">{r.vehicleLocation || "—"}</div>
                        {r.latitude != null && r.longitude != null ? (
                          <div className="mt-0.5 text-[11px] text-slate-500 tabular-nums">
                            {Number(r.latitude).toFixed(5)}, {Number(r.longitude).toFixed(5)}
                          </div>
                        ) : null}
                      </td>

                      <td className="px-4 py-4">
                        <div className="text-xs font-semibold text-slate-700 truncate">{r.openForBookingStates || "—"}</div>
                      </td>

                      <td className="px-4 py-4 text-right">
                        <div className="text-sm font-semibold text-slate-900 tabular-nums">{fmtInt(requestCount)}</div>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <div className="text-sm font-semibold text-slate-900 tabular-nums">{fmtInt(bookingCount)}</div>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <div className="text-sm font-semibold text-slate-900 tabular-nums">{fmtInt(completed)}</div>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <div className="text-sm font-semibold text-slate-900 tabular-nums">{fmtInt(unfulfilled)}</div>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <div className="text-sm font-semibold text-slate-900 tabular-nums">{fmtInt(upcoming)}</div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <Footer
          page={page}
          size={size}
          total={total}
          totalPages={totalPages}
          canPrev={canPrev}
          canNext={canNext}
          setPage={setPage}
          setSize={(newSize) => {
            setPage(0);
            setSize(newSize);
          }}
        />
      </div>
    </div>
  );
}
